import Button from 'tio-reimagined/components/button';
import { hash } from '@ember/helper';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import { Section } from 'tio-ui/components/layout';
import { trackedFunction } from 'reactiveweb/function';
import type AnswerProfileService from 'tio-employee/services/answer-profile';
import { VStack } from 'tio-ui/components/layout';
import { LinkTo } from '@ember/routing';
import {
  PhCheck,
  PhCircle,
  PhArrowRight,
  PhSparkle,
  PhClipboardText,
  PhUploadSimple,
  PhTrendUp,
  PhBuildings,
  PhCheckCircle,
  PhFileText,
  PhListChecks,
  PhQuestion,
} from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import type FeaturesService from 'tio-common/services/features';
import ForgivenessEstimatorResultsDashboardTable from 'tio-employee/components/forgiveness-estimator/results-dashboard-table';
import RecommendationsDashRecommendations from 'tio-employee/components/recommendations/dash-recommendations';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type AccountModel from 'tio-common/models/account';
import type PersonModel from 'tio-common/models/person';
import type PslfFormModel from 'tio-common/models/pslf-form';
import type PslfProfileModel from 'tio-common/models/pslf-profile';
import type RecomendationModel from 'tio-common/models/recommendation';
import { on } from '@ember/modifier';
import PslfMyDocumentsMyFormsTable from 'tio-employee/components/pslf/my-documents/my-forms-table';
import PslfBorrowerFormsTable from 'tio-employee/components/pslf/borrower-forms-table';
import type UploadProviderLogicService from 'tio-employee/services/upload-provider-logic';
import type { IntlService } from 'ember-intl';
import Card from 'tio-reimagined/components/card';
import FaqAccordion from 'tio-reimagined/components/faq-accordion';
import type { AccordionItem } from 'tio-reimagined/components/faq-accordion';
import SectionHeader from 'tio-reimagined/components/section-header';
import Text from 'tio-reimagined/components/text';
import HeroDashboard from 'tio-employee/components/pslf/hero-dashboard';
import PslfFormStatusAlerts from 'tio-employee/components/pslf/form-status-alerts';
import PslfRecommendations from 'tio-employee/components/pslf/recommendations';
import PslfQualifyingEmployment from 'tio-employee/components/pslf/qualifying-employment';
import PslfDebugPanel from 'tio-employee/components/reimagined/pslf/debug-panel';

function submittedForms(forms: PslfFormModel[]) {
  const submittedStates = ['submitted', 're_submitted'];
  return forms.filter((form) => submittedStates.includes(form.status));
}

interface S {
  Args: {
    model: {
      loanSummary: LoanSummary;
      accounts: AccountModel[];
      pslfProfile: PslfProfileModel;
      pslfForms: PslfFormModel[];
      person: PersonModel;
      mostRecentRecommendation?: RecomendationModel;
      isV2: boolean;
      debugLinkLoans: boolean;
    };
  };
}

export default class PslfDashboardIndexComponent extends Component<S> {
  @service declare uploadProviderLogic: UploadProviderLogicService;
  @service declare features: FeaturesService;
  @service declare intl: IntlService;
  @service('answer-profile') declare answerProfileService: AnswerProfileService;

  answerProfileState = trackedFunction(this, async () => {
    return await this.answerProfileService.findOrCreateAnswerProfileForCurrentUser();
  });

  get answerProfile() {
    return this.answerProfileState.value ?? undefined;
  }

  get showReimaginedLink() {
    return this.features.isEnabled('pslfReimaginedDashboard');
  }

  get uploadQueryParams() {
    return this.uploadProviderLogic.getUploadParams({
      isSlr: false,
      hasSyf: false,
      hasPslf: true,
      isWellnessOnly: false,
    });
  }

  get hasPslfForms() {
    return (this.args.model.pslfForms?.length ?? 0) > 0;
  }

  get debugLinkLoans() {
    return this.args.model.debugLinkLoans ?? false;
  }

  get hasActiveRecommendations() {
    const summaries = this.args.model.loanSummary?.summaryByLoanType || [];
    const allPlans = summaries.flatMap((s) => s.repaymentPlans);
    const hasMissing = allPlans.some((p) => p === 'noRepaymentPlan' || !p);

    // In-platform recommendations can't be checked off — always active if present
    const shouldLinkLoans = this.debugLinkLoans || this.args.model.person.needsToLinkLoans;
    if (shouldLinkLoans || hasMissing) return true;

    // External recommendations — active only if not checked off by user
    const profile = this.answerProfile;
    const shouldConsolidate = summaries.some((s) => s.loanType !== 'direct');
    const validPlans = ['icr', 'ibr', 'paye', 'repaye', 'fivePercentUndergrad'];
    const shouldChangeRepayment =
      !hasMissing && [...new Set(allPlans)].every((p) => !validPlans.includes(p));
    const needsToRecertify = !!this.args.model.mostRecentRecommendation;

    if (shouldConsolidate && !profile?.consolidatedLoansAt) return true;
    if (shouldChangeRepayment && !profile?.changedRepaymentPlanAt) return true;
    if (needsToRecertify && !profile?.recertifiedIncomeAt) return true;

    return false;
  }

  get faqItems(): AccordionItem[] {
    return [
      {
        question: this.intl.t('pslf.reimagined.faqs.what_is_pslf.question'),
        answer: this.intl.t('pslf.reimagined.faqs.what_is_pslf.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.eligibility_requirements.question'),
        answer: this.intl.t('pslf.reimagined.faqs.eligibility_requirements.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.qualifying_employers.question'),
        answer: this.intl.t('pslf.reimagined.faqs.qualifying_employers.answer'),
      },
      {
        question: this.intl.t('pslf.reimagined.faqs.how_to_apply.question'),
        answer: this.intl.t('pslf.reimagined.faqs.how_to_apply.answer'),
      },
    ];
  }

  <template>
    {{#if @model.isV2}}
      {{! ===== V2 Dashboard ===== }}
      <VStack @collapsed={{true}}>
        <HeroDashboard
          @pslfProfile={{@model.pslfProfile}}
          @pslfForms={{@model.pslfForms}}
          @loanSummary={{@model.loanSummary}}
          @person={{@model.person}}
          @mostRecentRecommendation={{@model.mostRecentRecommendation}}
        />

        {{#if this.hasPslfForms}}
          {{#if this.hasActiveRecommendations}}
            <PslfRecommendations
              @loanSummary={{@model.loanSummary}}
              @person={{@model.person}}
              @recommendation={{@model.mostRecentRecommendation}}
              @debugLinkLoans={{this.debugLinkLoans}}
              @answerProfile={{this.answerProfile}}
            />
          {{/if}}
        {{/if}}

        {{#if this.hasPslfForms}}
          <PslfFormStatusAlerts @forms={{@model.pslfForms}} class="space-y-3" />
        {{/if}}

        {{#if this.hasPslfForms}}
          <Card @padding="lg" @roundedXl={{true}} class="overflow-x-auto">
            <SectionHeader
              @icon={{PhFileText}}
              @title={{t "pslf.document_repo.my_pslf_forms"}}
              @iconClass="text-violet-600"
              @level={{2}}
            >
              <:trailing>
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  @size="sm"
                  {{on "click" (transitionTo "authenticated.pslf.dashboard.start-new")}}
                >
                  {{t "pslf.reimagined.submit_new_form"}}
                  <PhArrowRight @size={{14}} />
                </Button>
              </:trailing>
            </SectionHeader>
            <PslfBorrowerFormsTable @forms={{@model.pslfForms}} />
          </Card>
        {{else}}
          <Card @padding="lg" @roundedXl={{true}}>
            <SectionHeader
              @icon={{PhListChecks}}
              @title={{t "pslf.reimagined.steps.heading"}}
              @iconClass="text-violet-600"
              @level={{2}}
            />
            <div class="space-y-6">
              <div class="flex items-start gap-4">
                <div
                  class="shrink-0 w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900 flex items-center justify-center"
                >
                  <PhClipboardText @size={{20}} class="text-violet-600 dark:text-violet-400" />
                </div>
                <div>
                  <Text @weight="semibold" class="mb-1">
                    {{t "pslf.reimagined.steps.submit_form.title"}}
                  </Text>
                  <Text @variant="muted">
                    {{t "pslf.reimagined.steps.submit_form.description"}}
                  </Text>
                </div>
              </div>
              <div class="flex items-start gap-4">
                <div
                  class="shrink-0 w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center"
                >
                  <PhUploadSimple @size={{20}} class="text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <Text @weight="semibold" class="mb-1">
                    {{t "pslf.reimagined.steps.upload_data.title"}}
                  </Text>
                  <Text @variant="muted">
                    {{t "pslf.reimagined.steps.upload_data.description"}}
                  </Text>
                </div>
              </div>
              <div class="flex items-start gap-4">
                <div
                  class="shrink-0 w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center"
                >
                  <PhTrendUp @size={{20}} class="text-indigo-600 dark:text-indigo-400" />
                </div>
                <div>
                  <Text @weight="semibold" class="mb-1">
                    {{t "pslf.reimagined.steps.track_progress.title"}}
                  </Text>
                  <Text @variant="muted">
                    {{t "pslf.reimagined.steps.track_progress.description"}}
                  </Text>
                </div>
              </div>
            </div>
          </Card>
        {{/if}}

        {{! Eligibility Section (blank state) or Qualifying Employment (has forms) }}
        {{#if this.hasPslfForms}}
          <PslfQualifyingEmployment @forms={{@model.pslfForms}} />
        {{else}}
          <Card @padding="lg" @roundedXl={{true}}>
            <SectionHeader
              @icon={{PhCheckCircle}}
              @title={{t "pslf.reimagined.eligibility.heading"}}
              @iconClass="text-teal-600"
              @level={{2}}
            />
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div class="text-center">
                <div
                  class="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mx-auto mb-3"
                >
                  <PhBuildings @size={{24}} class="text-blue-600 dark:text-blue-400" />
                </div>
                <Text @weight="semibold" class="mb-1">
                  {{t "pslf.reimagined.eligibility.qualifying_employer.title"}}
                </Text>
                <Text @variant="muted" @size="sm">
                  {{t "pslf.reimagined.eligibility.qualifying_employer.description"}}
                </Text>
              </div>
              <div class="text-center">
                <div
                  class="w-12 h-12 rounded-full bg-teal-100 dark:bg-teal-900 flex items-center justify-center mx-auto mb-3"
                >
                  <PhCheckCircle @size={{24}} class="text-teal-600 dark:text-teal-400" />
                </div>
                <Text @weight="semibold" class="mb-1">
                  {{t "pslf.reimagined.eligibility.payments.title"}}
                </Text>
                <Text @variant="muted" @size="sm">
                  {{t "pslf.reimagined.eligibility.payments.description"}}
                </Text>
              </div>
              <div class="text-center">
                <div
                  class="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center mx-auto mb-3"
                >
                  <PhFileText @size={{24}} class="text-orange-600 dark:text-orange-400" />
                </div>
                <Text @weight="semibold" class="mb-1">
                  {{t "pslf.reimagined.eligibility.direct_loans.title"}}
                </Text>
                <Text @variant="muted" @size="sm">
                  {{t "pslf.reimagined.eligibility.direct_loans.description"}}
                </Text>
              </div>
            </div>
          </Card>
        {{/if}}

        {{#if this.hasPslfForms}}
          {{#unless this.hasActiveRecommendations}}
            <PslfRecommendations
              @loanSummary={{@model.loanSummary}}
              @person={{@model.person}}
              @recommendation={{@model.mostRecentRecommendation}}
              @debugLinkLoans={{this.debugLinkLoans}}
              @answerProfile={{this.answerProfile}}
            />
          {{/unless}}
        {{/if}}

        {{! FAQ Section }}
        <Card @padding="lg" @roundedXl={{true}}>
          <SectionHeader
            @icon={{PhQuestion}}
            @title={{t "pslf.reimagined.faqs.heading"}}
            @iconClass="text-amber-600"
            @level={{2}}
          />
          <FaqAccordion @items={{this.faqItems}} />
        </Card>

        <PslfDebugPanel />
      </VStack>
    {{else}}
      {{! ===== Original Dashboard ===== }}
      {{#if this.showReimaginedLink}}
        <div
          class="mb-4 flex items-center justify-between rounded-lg border border-violet-200 bg-violet-50 p-4 dark:border-violet-800 dark:bg-violet-950"
        >
          <div class="flex items-center gap-3">
            <PhSparkle @size={{20}} class="text-violet-600 dark:text-violet-400" />
            <span class="text-sm font-medium text-violet-900 dark:text-violet-100">
              {{t "pslf.reimagined.cta_banner.text"}}
            </span>
          </div>
          <LinkTo
            @route="authenticated.pslf.dashboard"
            @query={{hash v2="true"}}
            class="inline-flex items-center gap-1 rounded-md bg-violet-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-700 transition-colors"
          >
            {{t "pslf.reimagined.cta_banner.button"}}
            <PhArrowRight @size={{16}} />
          </LinkTo>
        </div>
      {{/if}}
      <Section>
        <:header>{{t "pslf.dashboard.get_started"}}</:header>
        <:subHeader>{{t "pslf.dashboard.optimize_your_pslf"}}</:subHeader>
        <:body>
          <ul role="list" class="divide-y divide-gray-100">
            <li class="flex justify-between gap-x-36 py-5">
              <div class="flex min-w-0 gap-x-4">
                {{#if @model.accounts.length}}
                  <PhCheck @size="20" class="text-green-600" />
                {{else}}
                  <PhCircle @weight="fill" @size="20" class="text-gray-400" />
                {{/if}}
                <div class="min-w-0 flex-auto">
                  <p class="font-semibold text-gray-900">{{t "pslf.dashboard.add_loans_title"}}</p>
                  <p class="mt-1 text-gray-500">{{t "pslf.dashboard.add_loans_description"}}</p>

                </div>
              </div>
              <div class="shrink-0 sm:items-end">
                <Button
                  {{on
                    "click"
                    (transitionTo
                      "authenticated.observability.upload" (hash queryParams=this.uploadQueryParams)
                    )
                  }}
                  @intent="primary"
                  @appearance="outlined"
                >
                  {{t "pslf.dashboard.add_loans" numLoans=@model.accounts.length}}
                </Button>
              </div>
            </li>
            <li class="flex justify-between gap-x-36 py-5">
              <div class="flex min-w-0 gap-x-4">
                {{#if (submittedForms @model.pslfProfile.pslfForms)}}
                  <PhCheck @size="20" class="text-green-600" />
                {{else}}
                  <PhCircle @weight="fill" @size="20" class="text-gray-400" />
                {{/if}}
                <div class="min-w-0 flex-auto">
                  <p class="font-semibold text-gray-900">{{t "pslf.dashboard.submit_your_form"}}</p>
                  <p class="mt-1 text-gray-500">{{t "pslf.dashboard.track_payments"}} </p>
                </div>
              </div>
              <div class="shrink-0 sm:items-end">
                <Button
                  {{on "click" (transitionTo "authenticated.pslf.dashboard.start-new")}}
                  @intent="primary"
                  @appearance="outlined"
                >
                  {{t "pslf.dashboard.form"}}
                </Button>
              </div>
            </li>
            <li class="flex justify-between gap-x-36 py-5">
              <div class="flex min-w-0 gap-x-4">
                {{#if @model.mostRecentRecommendation}}
                  <PhCheck @size="20" class="text-green-600" />
                {{else}}
                  <PhCircle @weight="fill" @size="20" class="text-gray-400" />
                {{/if}}
                <div class="min-w-0 flex-auto">
                  <p class="font-semibold text-gray-900">{{t "pslf.dashboard.try_estimator"}}</p>
                  <p class="mt-1 text-gray-500">{{t "pslf.dashboard.try_estimator_description"}}</p>
                </div>
              </div>
              <div class="shrink-0 sm:items-end">
                <Button
                  {{on
                    "click"
                    (transitionTo "authenticated.pslf.dashboard.forgiveness-estimator.index")
                  }}
                  @intent="primary"
                  @appearance="outlined"
                >{{t "pslf.dashboard.estimator"}}</Button>
              </div>
            </li>
          </ul>
        </:body>
      </Section>

      <Section class="overflow-x-auto">
        <:header>{{t "pslf.document_repo.my_pslf_forms"}}</:header>
        <:body>
          <PslfMyDocumentsMyFormsTable @limit={{10}} @employee={{@model.pslfProfile.employee}} />
        </:body>
      </Section>

      {{#if @model.mostRecentRecommendation}}
        <RecommendationsDashRecommendations
          @loanSummary={{@model.loanSummary}}
          @person={{@model.person}}
          @recommendation={{@model.mostRecentRecommendation}}
          @source="pslf"
        />
        <ForgivenessEstimatorResultsDashboardTable
          @recommendation={{@model.mostRecentRecommendation}}
          @programType="pslf"
        />
      {{/if}}
    {{/if}}
  </template>
}
